/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { type gestureTypes, useContextMenu } from '#hooks/PRIVATE_useVisContextMenu';
import type {
  PieChartContextMenuContext,
  PieChartContextMenuConfig,
  PieChartItemData
} from './pieChart.types';
import { ItemInfo } from '#hooks/PRIVATE_usePieChartNav';

import { getInfo } from '#utils/UNSAFE_visUtils';

type Props<D> = {
  offset: {
    mainAxis: number;
    crossAxis: number;
  };
  rootRef: RefObject<HTMLDivElement>;
  contextMenuConfig?: PieChartContextMenuConfig<D>;
  focusedItemInfo: ItemInfo;
  items: D[];
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
  width: number;
  isRtl: boolean;
};

export const usePieChartContextMenu = <K, D extends PieChartItemData<K>>({
  offset,
  rootRef,
  contextMenuConfig,
  focusedItemInfo,
  items,
  onContextMenuDismissed,
  width,
  isRtl
}: Props<D>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<PieChartContextMenuContext<D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      if (gesture === 'keyboard') {
        const itemIndex = focusedItemInfo.itemIndex;
        const data = items[itemIndex];
        const context = {
          type: 'item' as const,
          data
        };
        return {
          type: 'offset',
          context,
          offsetValue: offset,
          isRtl,
          width
        };
      }

      const itemIndex = getInfo(rootRef, target as HTMLElement)?.itemIndex;
      const data = itemIndex !== undefined ? items[itemIndex] : null;

      let context;
      if (data) {
        context = {
          type: 'item' as const,
          data: data
        };
      } else if (rootRef.current?.children[1].contains(target)) {
        context = { type: 'centerLabel' as const };
      } else {
        context = { type: 'background' as const };
      }
      return {
        type: 'pointer',
        context
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig //If there is no context menu renderer, we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
